import { Injectable } from '@angular/core';
import { FlashService } from '@app/services/flash.service';
import { AlarmService } from '@app/services/api/alarm.service';
import {
  AddAll,
  LoadFail,
  LoadSuccess,
  AlarmActionTypes,
  AddOne,
  Create,
  CreateFail,
  CreateSuccess,
  UpdateAlarm,
  UpdateAlarmSuccess,
  UpdateFail,
  UpdateAlarmOne,
  DeleteAlarm,
  DeleteAlarmSuccess,
  DeleteAlarmFail
} from '@core/actions/alarm.actions';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Alarm } from '@app/models/alarm';

@Injectable()
export class AlarmEffects {
  constructor(
    private actions$: Actions, 
    private alarmsApi: AlarmService, 
    private fs: FlashService
  ) {}

  @Effect()
  load$: Observable<Action> = this.actions$.pipe(
    ofType(AlarmActionTypes.Load),
    switchMap(action =>
      this.alarmsApi.get(action).pipe(
        map(alarms => new LoadSuccess(alarms)),
        catchError(error => of(new LoadFail(error)))
      )
    )
  );

  @Effect()
  loadSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<LoadSuccess>(AlarmActionTypes.LoadSuccess),
    map(action => new AddAll(action.alarms))
  );

  @Effect()
  create$: Observable<Action> = this.actions$.pipe(
    ofType<Create>(AlarmActionTypes.Create),
    map(action => action.alarm),
    switchMap(alarm => this.alarmsApi.create(alarm).pipe(
      map((savedAlarm: Alarm) => new CreateSuccess(savedAlarm)),
      catchError(error => of(new CreateFail(error))))));

  @Effect()
  createSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<CreateSuccess>(AlarmActionTypes.CreateSuccess),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Alarma creada' });
      return new AddOne(action.alarm);
    })
  );

  @Effect()
  update$: Observable<Action> = this.actions$.pipe(
    ofType<UpdateAlarm>(AlarmActionTypes.UpdateAlarm),
    map(action => [action.id, action.alarm]),
    switchMap(([id, data]) => {
      return this.alarmsApi.update(id, data).pipe(
        map((hangers: Alarm) => new UpdateAlarmSuccess(id, hangers)),
        catchError(error => of(new UpdateFail(error)))
      );
    })
  );

  @Effect()
  updateSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<UpdateAlarmSuccess>(AlarmActionTypes.UpdateAlarmSuccess),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Alarma actualizada' });
      return new UpdateAlarmOne(action.data);
    })
  );

@Effect({ dispatch: false })
  updateFail$ = this.actions$.pipe(
    ofType<UpdateFail>(AlarmActionTypes.UpdateFail),
    map(action => action.error),
    tap(res =>
      this.fs.emit({
        message: 'Todos los  campos son necesarios',
        type: 'danger'
      })
    )
  );
  
  @Effect()
  deleteAlarm$: Observable<Action> = this.actions$.pipe(
    ofType<DeleteAlarm>(AlarmActionTypes.DeleteAlarm),
    map(action => [action.alarmId]),
    switchMap(([alarmId]) => {
      return this.alarmsApi.delete(alarmId).pipe(
        map((hangers: any) => new DeleteAlarmSuccess()),
        catchError(error => of(new DeleteAlarmFail(error)))
      );
    })
  );

  @Effect({ dispatch: false })
  deleteAlarmSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<DeleteAlarmSuccess>(AlarmActionTypes.DeleteAlarmSuccess),
    map(action => action.data),
    tap(res =>
      this.fs.emit({
        message: 'Alarma eliminada',
        type: 'success'
      })
    )
  );

  @Effect({ dispatch: false })
  deleteAlarmFail$ = this.actions$.pipe(
    ofType<DeleteAlarmFail>(AlarmActionTypes.DeleteAlarmFail),
    map(action => action.error),
    tap(res =>
      this.fs.emit({
        message: 'No se pudo crear la alarma',
        type: 'danger'
      })
    )
  );
}
